import type { ChangeEvent, FC, ReactNode } from "react";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { useFeatureFlags } from "@jti/commerce/shared";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { Separator } from "@jti/core-components/components/Separator";
import { newrelic } from "@jti/fe-clients";
import { customPaymentMethods, type PaymentMethod } from "@jti/fe-common";
import { useDispatch } from "@jti/store/reduxHooks";
import { checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui";

import { usePaymentProviderContext } from "@checkout-v2/features/payment";
import { MAGENTO_PAYMENT_METHOD_COMMON, MAGENTO_PAYMENT_METHOD_PAY_ON_DELIVERY } from "@checkout-v2/shared";
import { useOrderLimit } from "@checkout-v2/shared/hooks/useOrderLimit";
import { getErrorMessage } from "@checkout-v2/shared/utils";

import { FIELD_SELECTORS, SecuredFieldsForm } from "../../forms/SecuredFieldsForm";
import { useSecuredFieldsSession } from "../../hooks/useSecuredFieldsSession";
import { shouldShowPodSeparator } from "../../utils/shouldShowPodSeparator";
import { PaymentMethodOption } from "../PaymentMethodOption";

import styles from "./PaymentMethodsSection.module.scss";

export type PaymentOption = Omit<PaymentMethod, "enabled"> & {
  disabled?: boolean;
};

type PaymentMethodsSectionProps = {
  paymentOptions: PaymentOption[];
  selectedPaymentMethodCode?: string;
  isError?: boolean;
  resetError?: () => void;
  error?: unknown;
  children?: ReactNode;
  isLoading?: boolean;
  submitButtonText?: string;
  onSubmit?: (selectedPaymentMethodCode: string, magentoPaymentMethodCode: string) => boolean | Promise<boolean>;
};

export const PaymentMethodsSection: FC<PaymentMethodsSectionProps> & {
  CartLoyaltyInfo: FC<{ children: ReactNode }>;
} = ({
  paymentOptions,
  selectedPaymentMethodCode,
  isError,
  resetError,
  error,
  isLoading,
  submitButtonText,
  children,
  onSubmit,
}) => {
  const { __ } = useTranslations();
  const config = useConfig();
  const dispatch = useDispatch();
  const { isOrderLimitExceeded } = useOrderLimit();
  const { preloadAdapter, adapterStatus, checkSecuredFieldsSupport } = usePaymentProviderContext();
  const { ffCheckoutV2EnableIteration3 } = useFeatureFlags();

  const enabledOptions = paymentOptions.filter((option) => !option.disabled);

  const areAllOptionsDisabled = enabledOptions.length === 0;

  const [selectedMethodCode, setSelectedMethodCode] = useState<string | undefined>(() => {
    const isValidSelection =
      selectedPaymentMethodCode != null && enabledOptions.some((o) => o.code === selectedPaymentMethodCode);

    if (isValidSelection) {
      return selectedPaymentMethodCode;
    }

    return enabledOptions.length > 0 ? enabledOptions[0].code : undefined;
  });
  const [isCardSaved, setIsCardSaved] = useState(false);

  const handleAdapterLoading = useCallback(
    async (methodCode: string) => {
      try {
        // Ensure the adapter error is reset before re-submitting
        // adapterStatus is set in the preloadAdapter body,
        if (adapterStatus === "error") {
          dispatch(checkoutV2Slice.actions.clearError());
        }
        await preloadAdapter(methodCode);
      } catch (error) {
        newrelic.noticeError(error as Error, {
          context: "PaymentMethodsSection - Adapter Loading",
        });

        dispatch(
          checkoutV2Slice.actions.setError({
            message: __("cart.checkout.payment_methods_form.error.adapter_load_error"),
            type: "unknown",
          })
        );
        throw error;
      }
    },
    [adapterStatus, dispatch, preloadAdapter, __]
  );

  useEffect(() => {
    if (!ffCheckoutV2EnableIteration3) return;
    if (adapterStatus !== "idle") return;
    if (!selectedMethodCode || selectedMethodCode === customPaymentMethods.payOnDelivery) return;

    handleAdapterLoading(selectedMethodCode).catch(() => {
      // Error already dispatched inside handleAdapterLoading
    });
  }, [ffCheckoutV2EnableIteration3, adapterStatus, selectedMethodCode, handleAdapterLoading]);

  const showSecuredFields = useMemo(
    () =>
      !!ffCheckoutV2EnableIteration3 &&
      adapterStatus === "ready" &&
      selectedMethodCode != null &&
      selectedMethodCode !== customPaymentMethods.payOnDelivery &&
      checkSecuredFieldsSupport(selectedMethodCode),
    [ffCheckoutV2EnableIteration3, adapterStatus, selectedMethodCode, checkSecuredFieldsSupport]
  );

  const {
    sessionState: securedFieldsState,
    validateCard,
    resetErrors: resetSecuredFieldsErrors,
  } = useSecuredFieldsSession({
    enabled: showSecuredFields,
    paymentMethodCode: selectedMethodCode ?? "",
    fieldSelectors: FIELD_SELECTORS,
    locale: config.localeCode,
  });

  const isSecuredFieldsLoading =
    securedFieldsState.status === "initializing" ||
    securedFieldsState.status === "loading-sdk" ||
    securedFieldsState.status === "configuring";

  // Inputs must be in DOM before configure() fires
  const isSecuredFieldsReady =
    securedFieldsState.status === "initializing" ||
    securedFieldsState.status === "loading-sdk" ||
    securedFieldsState.status === "configuring" ||
    securedFieldsState.status === "ready" ||
    securedFieldsState.status === "submitting" ||
    securedFieldsState.status === "validated" ||
    securedFieldsState.status === "field-errors" ||
    securedFieldsState.status === "general-validation-error";

  const magentoPaymentMethod =
    selectedMethodCode === MAGENTO_PAYMENT_METHOD_PAY_ON_DELIVERY
      ? MAGENTO_PAYMENT_METHOD_PAY_ON_DELIVERY
      : MAGENTO_PAYMENT_METHOD_COMMON;

  const setSectionsState = useCallback(() => {
    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "completed",
        orderConfirmationSection: "active",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (securedFieldsState.status === "validated" && selectedMethodCode) {
      dispatch(
        checkoutV2Slice.actions.setData({
          paymentMethodCode: selectedMethodCode,
          securedFieldsPayment: {
            sessionId: securedFieldsState.sessionId,
            paymentMethodCode: selectedMethodCode,
            tokenize: isCardSaved,
            cardDetails: securedFieldsState.cardDetails,
          },
        })
      );
      setSectionsState();
    }
  }, [securedFieldsState, selectedMethodCode, isCardSaved, dispatch, setSectionsState]);

  const onOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (isError && resetError) {
      resetError();
    }

    if (value !== selectedMethodCode) {
      setIsCardSaved(false);
      setSelectedMethodCode(value);
    }
  };

  const onSectionSubmit = async () => {
    if (!selectedMethodCode) {
      return;
    }

    let canProceed = true;

    if (onSubmit) {
      canProceed = await onSubmit(selectedMethodCode, magentoPaymentMethod);
    }

    if (!canProceed) {
      return;
    }

    dispatch(
      checkoutV2Slice.actions.setData({
        paymentMethodCode: selectedMethodCode,
      })
    );

    if (selectedMethodCode === customPaymentMethods.payOnDelivery) {
      setSectionsState();
      return;
    }

    if (showSecuredFields) {
      resetSecuredFieldsErrors();
      validateCard();
      return;
    }

    try {
      await handleAdapterLoading(selectedMethodCode);
    } catch {
      return;
    }

    setSectionsState();
  };

  let loyaltyInfoSlot: ReactNode = null;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<{ children?: ReactNode }>(child)) {
      return;
    }

    if (child.type === PaymentMethodsSection.CartLoyaltyInfo) {
      loyaltyInfoSlot = child.props.children;
    }
  });

  return (
    <div className={styles.container}>
      {areAllOptionsDisabled && (
        <AlertBanner
          tone="error"
          title={__("cart.order.payment_method.no_methods.title")}
          description={__("cart.order.payment_method.no_methods.description")}
          actions={[
            {
              href: config.CONTACT_US_URL,
              label: __("cart.order.payment_method.no_methods.cta"),
            },
          ]}
        />
      )}

      {isError && (
        <AlertBanner tone="error" description={getErrorMessage(error) ?? __("cart.order.payment_method.error")} />
      )}

      {securedFieldsState.status === "error" && (
        <AlertBanner tone="error" description={__("cart.checkout.secured_fields.system_error.general")} />
      )}

      <Card
        variant="level-1"
        title={__("cart.order.payment_method.title")}
        description={__("cart.order.payment_method.choose")}
      >
        {loyaltyInfoSlot}
        <div className={styles.paymentMethods}>
          {paymentOptions.map((option, index) => (
            <Fragment key={option.code}>
              {shouldShowPodSeparator({
                option,
                index,
                paymentOptionsLength: paymentOptions.length,
                isIteration3Enabled: ffCheckoutV2EnableIteration3,
              }) && <Separator text={__("cart.order.payment_method.pod_separator")} />}

              <PaymentMethodOption
                paymentMethodCode={option.code}
                title={option.title}
                icon={option.icon}
                tooltip={option.definition}
                disabled={option.disabled}
                checked={option.code === selectedMethodCode}
                onChange={onOptionChange}
              >
                {showSecuredFields && option.code === selectedMethodCode && (
                  <>
                    <SecuredFieldsForm
                      errors={securedFieldsState.status === "field-errors" ? securedFieldsState.errors : null}
                      hasGeneralValidationError={securedFieldsState.status === "general-validation-error"}
                      isLoading={isSecuredFieldsLoading}
                      isReady={isSecuredFieldsReady}
                      isCardSaved={isCardSaved}
                      onSaveCardChange={setIsCardSaved}
                    />
                  </>
                )}
              </PaymentMethodOption>
            </Fragment>
          ))}
        </div>
      </Card>

      {showSecuredFields && securedFieldsState.status === "general-validation-error" && (
        <AlertBanner tone="error" description={__("cart.checkout.secured_fields.system_error.general")} />
      )}

      <Button
        type="button"
        pending={
          isLoading ||
          adapterStatus === "loading" ||
          isSecuredFieldsLoading ||
          securedFieldsState.status === "submitting"
        }
        onClick={onSectionSubmit}
        disabled={!selectedMethodCode || isOrderLimitExceeded}
      >
        {submitButtonText ?? __("cart.order.payment_method.submit")}
      </Button>
    </div>
  );
};

PaymentMethodsSection.CartLoyaltyInfo = function CartLoyaltyInfo({ children }: { children: ReactNode }) {
  return <>{children}</>;
};
